import React from 'react'
import useSWR from 'swr'
import { fetcher } from 'api/swr'
import { create } from 'zustand'
import { useQueryState } from 'hooks/useQueryState'
import Loader from '@/components/ui/Loader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import Selector from 'components/ui/Selector'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { SpineAvatar } from '../types'
import { SpineAvatarItem } from './components/SpineAvatarItem'

const SPINE_AVATAR_STATE_ITEMS = [
  { code: '0', name: 'local' },
  { code: '401', name: 'public' },
]

const SPINE_AVATAR_ORDER_ITEMS = [
  { code: 'updateTime', name: 'updateTime' },
  { code: 'reCreateCount', name: 'reCreateCount' },
]

interface ListPageState {
  after: string | null
  afters: string[]
  goAfter: (nextAfter: string | null) => void
  resetPage: () => void
}

export const useSpineAvatarsListPage = create<ListPageState>((set) => ({
  after: null,
  afters: [],
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null, afters: [] }
      }
      const index = state.afters.findIndex((a) => a === nextAfter)
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] }
      }
      return { after: nextAfter }
    }),
  resetPage: () => set({ after: null, afters: [] }),
}))

const SpineAvatarsList = () => {
  const [tid, setTid] = useQueryState('tid', { defaultValue: '' })
  const [stateFilter, setStateFilter] = useQueryState('state', {
    defaultValue: '401',
  })
  const [avatarId, setAvatarId] = useQueryState('id', { defaultValue: '' })
  const [uid, setUid] = useQueryState('uid', { defaultValue: '' })
  const [aid, setAid] = useQueryState('aid', { defaultValue: '' })
  const [order, setOrder] = useQueryState('order', {
    defaultValue: 'updateTime',
  })
  const [simple, setSimple] = useQueryState('simple', { defaultValue: '0' })

  const after = useSpineAvatarsListPage((s) => s.after)
  const afters = useSpineAvatarsListPage((s) => s.afters)
  const goAfter = useSpineAvatarsListPage((s) => s.goAfter)
  const resetPage = useSpineAvatarsListPage((s) => s.resetPage)

  const filterKey = `${tid}\0${stateFilter}\0${avatarId}\0${uid}\0${aid}\0${order}\0${simple}`
  const prevFilterKeyRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (prevFilterKeyRef.current === null) {
      prevFilterKeyRef.current = filterKey
      return
    }
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey
      resetPage()
    }
  }, [filterKey, resetPage])

  const params = React.useMemo(
    () => ({
      ...(tid ? { tid } : {}),
      limit: 40,
      after,
      ...(avatarId ? { id: avatarId } : {}),
      ...(uid ? { uid } : {}),
      ...(aid ? { aid } : {}),
      state: stateFilter,
      simple: Number(simple),
      order,
    }),
    [tid, after, avatarId, uid, aid, stateFilter, simple, order],
  )

  const { data, isLoading, isValidating, mutate } = useSWR<SpineAvatar[]>(
    ['/spine-avatars', params],
    fetcher,
  )

  const nextAfter = data?.[data.length - 1]?.id
  const isLoadingData = isLoading || isValidating

  return (
    <div className="flex-1 space-y-4">
      <h2 className="text-3xl font-bold tracking-tight">Spine Avatars</h2>

      {isLoadingData && <Loader>Loading avatars...</Loader>}
      {!isLoadingData && (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <MyPagination
              nextAfter={nextAfter}
              after={after}
              goAfter={goAfter}
              afters={afters}
              className="w-auto m-0"
            />
            <MyRefetchButton onClick={mutate} />
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-tid" className="text-sm font-medium shrink-0">
                Tid:
              </label>
              <div className="relative">
                <Input
                  id="spine-avatars-tid"
                  type="text"
                  value={tid}
                  onChange={(e) => setTid(e.target.value)}
                  className="w-[180px]"
                />
                {tid ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setTid('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    aria-label="清除 Tid"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-state" className="text-sm font-medium">
                State:
              </label>
              <Selector
                id="spine-avatars-state"
                datas={SPINE_AVATAR_STATE_ITEMS}
                labelName="State"
                value={stateFilter}
                update={(v) => setStateFilter(String(v))}
                width="w-[180px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-order" className="text-sm font-medium">
                Order:
              </label>
              <Selector
                id="spine-avatars-order"
                datas={SPINE_AVATAR_ORDER_ITEMS}
                labelName="Order"
                value={order}
                update={(v) => setOrder(String(v))}
                width="w-[180px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="spine-avatars-simple"
                checked={simple === '1'}
                onCheckedChange={(checked) => setSimple(checked === true ? '1' : '0')}
              />
              <label
                htmlFor="spine-avatars-simple"
                className="text-sm font-medium cursor-pointer"
              >
                Simple
              </label>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-id" className="text-sm font-medium">
                ID:
              </label>
              <div className="relative">
                <Input
                  id="spine-avatars-id"
                  type="text"
                  placeholder="Avatar ID"
                  value={avatarId}
                  onChange={(e) => setAvatarId(e.target.value)}
                  className="w-[300px]"
                />
                {avatarId ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setAvatarId('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    aria-label="清除 ID"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-uid" className="text-sm font-medium shrink-0">
                Uid:
              </label>
              <div className="relative">
                <Input
                  id="spine-avatars-uid"
                  type="text"
                  value={uid}
                  onChange={(e) => setUid(e.target.value)}
                  className="w-[300px]"
                />
                {uid ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setUid('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    aria-label="清除 Uid"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="spine-avatars-aid" className="text-sm font-medium shrink-0">
                Aid:
              </label>
              <div className="relative">
                <Input
                  id="spine-avatars-aid"
                  type="text"
                  placeholder="Parent Avatar ID"
                  value={aid}
                  onChange={(e) => setAid(e.target.value)}
                  className="w-[300px]"
                />
                {aid ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setAid('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    aria-label="清除 Aid"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {!data || data.length === 0 ? (
            <Card>
              <CardContent className="p-6">
                <p className="text-muted-foreground">暂无符合条件的 Spine Avatar。</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pb-0">
                <div className="grid grid-cols-8 gap-2 my-6">
                  {data.map((avatar) => (
                    <SpineAvatarItem
                      key={avatar.id}
                      avatar={avatar}
                      tid={tid || undefined}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default SpineAvatarsList
